import { createFileRoute } from '@tanstack/react-router'
import {
  Button,
  Carousel,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Upload,
  message,
} from 'antd'
import { useState } from 'react'
import { SearchOutlined, UploadOutlined } from '@ant-design/icons'
import { getPagination } from '../components/TablePagination'
import {
  useAddCarousel,
  useCarousels,
  useDeleteCarousel,
  useEnabledCarousels,
  useUpdateCarousel,
  useUpdateCarouselStatus,
} from '../hooks/useCarousels'
import { useAllCourses } from '../hooks/useCourses'
import type { ColumnsType } from 'antd/es/table'
import type { UploadFile } from 'antd/es/upload/interface'
import type { FilterDropdownProps } from 'antd/es/table/interface'
import type { CarouselVo } from '../services/carouselService'

export const Route = createFileRoute('/carousel')({
  component: () => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [currentCarousel, setCurrentCarousel] = useState<CarouselVo | null>(
      null,
    )
    const [form] = Form.useForm()
    const [fileList, setFileList] = useState<Array<UploadFile>>([])
    const [pageNum, setPageNum] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [filters, setFilters] = useState<{
      name?: string
      courseId?: number
      status?: number
    }>({})

    // API hooks
    const carouselQuery = useCarousels({
      pageNum,
      pageSize,
      ...filters,
    })
    const enabledCarouselsQuery = useEnabledCarousels()
    const coursesQuery = useAllCourses()
    const addCarouselMutation = useAddCarousel()
    const updateCarouselMutation = useUpdateCarousel()
    const deleteCarouselMutation = useDeleteCarousel()
    const updateStatusMutation = useUpdateCarouselStatus()

    const loading = carouselQuery.isLoading || enabledCarouselsQuery.isLoading
    const carousels = carouselQuery.data?.records || []
    const enabledCarousels = enabledCarouselsQuery.data || []
    const courses = coursesQuery.data || []

    // 搜索筛选组件
    const getSearchFilterDropdown =
      (key: keyof typeof filters, placeholder: string) =>
      (props: FilterDropdownProps) => {
        const { setSelectedKeys, selectedKeys, confirm, clearFilters } = props
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder={placeholder}
              value={selectedKeys[0] as string}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => {
                const value = selectedKeys[0] as string
                setFilters((prev) => ({
                  ...prev,
                  [key]: value || undefined,
                }))
                setPageNum(1) // 重置到第一页
                confirm({ closeDropdown: true })
              }}
              style={{ marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  const value = selectedKeys[0] as string
                  setFilters((prev) => ({
                    ...prev,
                    [key]: value || undefined,
                  }))
                  setPageNum(1) // 重置到第一页
                  confirm({ closeDropdown: true })
                }}
                size="small"
                style={{ width: 90 }}
              >
                搜索
              </Button>
              <Button
                onClick={() => {
                  setFilters((prev) => ({
                    ...prev,
                    [key]: undefined,
                  }))
                  setPageNum(1) // 重置到第一页
                  clearFilters && clearFilters()
                  confirm({ closeDropdown: true })
                }}
                size="small"
                style={{ width: 90 }}
              >
                重置
              </Button>
            </Space>
          </div>
        )
      }

    const columns: ColumnsType<CarouselVo> = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: '名称',
        dataIndex: 'name',
        key: 'name',
        filteredValue: filters.name ? [filters.name] : undefined,
        filterDropdown: getSearchFilterDropdown('name', '搜索轮播图名称'),
        filterIcon: (filtered: boolean) => (
          <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
      },
      {
        title: '轮播图',
        dataIndex: 'imageUrl',
        key: 'imageUrl',
        render: (imageUrl: string) => (
          <img
            src={imageUrl}
            alt="轮播图"
            className="w-20 h-12 object-cover rounded"
          />
        ),
      },
      {
        title: '排序',
        dataIndex: 'sortOrder',
        key: 'sortOrder',
        sorter: (a, b) => a.sortOrder - b.sortOrder,
        width: 80,
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        filters: [
          { text: '启用', value: 1 },
          { text: '禁用', value: 0 },
        ],
        filteredValue:
          filters.status !== undefined ? [filters.status] : undefined,
        render: (status: number, record) => (
          <Switch
            checked={status === 1}
            onChange={(checked) =>
              updateStatusMutation.mutate({
                id: record.id,
                status: checked ? 1 : 0,
              })
            }
            loading={updateStatusMutation.isPending}
          />
        ),
        width: 80,
      },
      {
        title: '关联课程',
        dataIndex: 'courseName',
        key: 'courseName',
        filters: courses.map((course) => ({
          text: course.name,
          value: course.id,
        })),
        filteredValue:
          filters.courseId !== undefined ? [filters.courseId] : undefined,
      },
      // {
      //   title: '创建时间',
      //   dataIndex: 'createdTime',
      //   key: 'createdTime',
      //   width: 150,
      // },
      {
        title: '操作',
        key: 'action',
        render: (_, record) => (
          <Space>
            <Button size="small" onClick={() => handleEdit(record)}>
              编辑
            </Button>
            <Button
              size="small"
              danger
              onClick={() => handleDelete(record.id)}
              loading={deleteCarouselMutation.isPending}
            >
              删除
            </Button>
          </Space>
        ),
        width: 120,
      },
    ]

    const handleAdd = () => {
      setCurrentCarousel(null)
      form.resetFields()
      setFileList([])
      setIsModalOpen(true)
    }

    const handleEdit = (carousel: CarouselVo) => {
      setCurrentCarousel(carousel)
      form.setFieldsValue({
        name: carousel.name,
        jumpLink: carousel.jumpLink,
        courseId: carousel.courseId,
        sortOrder: carousel.sortOrder,
      })
      setFileList([])
      setIsModalOpen(true)
    }

    const handleDelete = (id: number) => {
      Modal.confirm({
        title: '确认删除',
        content: '确定要删除这个轮播图吗？',
        onOk: () => deleteCarouselMutation.mutate(id),
      })
    }

    const handleOk = () => {
      form.validateFields().then((values) => {
        const imageFile = fileList[0]?.originFileObj

        if (currentCarousel) {
          // 编辑
          updateCarouselMutation.mutate(
            {
              id: currentCarousel.id,
              name: values.name,
              image: imageFile,
              jumpLink: values.jumpLink,
              courseId: values.courseId,
              sortOrder: values.sortOrder,
            },
            {
              onSuccess: () => setIsModalOpen(false),
            },
          )
        } else {
          // 新增
          if (!imageFile) {
            message.error('请选择轮播图片')
            return
          }
          addCarouselMutation.mutate(
            {
              name: values.name,
              image: imageFile,
              jumpLink: values.jumpLink,
              courseId: values.courseId,
              sortOrder: values.sortOrder,
            },
            {
              onSuccess: () => setIsModalOpen(false),
            },
          )
        }
      })
    }

    const handleTableChange = (pagination: any, tableFilters: any) => {
      setPageNum(pagination.current)
      setPageSize(pagination.pageSize)

      // 处理表格筛选变化
      setFilters({
        name: tableFilters.name?.[0] || undefined,
        courseId: tableFilters.courseName?.[0] || undefined,
        status: tableFilters.status?.[0] || undefined,
      })
    }

    if (loading) {
      return (
        <div className="p-4">
          <Spin size="large" />
        </div>
      )
    }

    return (
      <div className="p-4 space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">轮播图管理</h2>
          <Button type="primary" onClick={handleAdd}>
            添加轮播图
          </Button>
        </div>

        {/* 轮播图预览 */}
        {enabledCarousels.length > 0 && (
          <div className="bg-white p-4 rounded shadow">
            <h3 className="text-md font-semibold mb-4">轮播图预览</h3>
            <Carousel autoplay adaptiveHeight={true}>
              {enabledCarousels.map((carousel) => (
                <div key={carousel.id}>
                  <img
                    src={carousel.imageUrl}
                    alt={carousel.name}
                    className="w-auto h-64 object-cover"
                  />
                </div>
              ))}
            </Carousel>
          </div>
        )}

        {/* 轮播图列表 */}
        <Table
          columns={columns}
          dataSource={carousels}
          rowKey="id"
          bordered
          loading={carouselQuery.isLoading}
          pagination={getPagination(
            pageNum,
            pageSize,
            carouselQuery.data?.total || 0,
          )}
          onChange={handleTableChange}
        />

        {/* 添加/编辑弹窗 */}
        <Modal
          title={currentCarousel ? '编辑轮播图' : '添加轮播图'}
          open={isModalOpen}
          onOk={handleOk}
          onCancel={() => setIsModalOpen(false)}
          confirmLoading={
            addCarouselMutation.isPending || updateCarouselMutation.isPending
          }
          width={600}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label="轮播图名称"
              rules={[{ required: true, message: '请输入轮播图名称' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="轮播图片"
              rules={
                !currentCarousel
                  ? [{ required: true, message: '请选择轮播图片' }]
                  : []
              }
            >
              <Upload
                listType="picture"
                fileList={fileList}
                onChange={({ fileList: newFileList }) =>
                  setFileList(newFileList)
                }
                beforeUpload={() => false} // 阻止自动上传
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>选择图片</Button>
              </Upload>
              {currentCarousel && (
                <div className="mt-2">
                  <img
                    src={currentCarousel.imageUrl}
                    alt="当前图片"
                    className="w-32 h-20 object-cover rounded"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    不选择新图片将保持原图片
                  </p>
                </div>
              )}
            </Form.Item>
            <Form.Item
              name="courseId"
              label="关联课程"
              rules={[{ required: true, message: '请选择关联课程' }]}
            >
              <Select
                placeholder="请选择关联课程"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {courses.map((course) => (
                  <Select.Option key={course.id} value={course.id}>
                    {course.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="sortOrder" label="排序序号">
              <InputNumber min={0} placeholder="请输入排序序号" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    )
  },
})
